import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import HttpClient from '@/api/HttpClient';

const httpClient = new HttpClient();

interface GenerateReportParams {
  locationId: number;
  reportId?: number;
  preview?: boolean;
  sendToPrimaryContact?: boolean;
  serviceDate?: string;
  signatureResource?: any;
  identityResource?: any;
}

interface GenerateReportResponse {
  html: string;
  success: boolean;
}

interface PreviewReportParams {
  locationId: number;
  reportId?: number;
  serviceDate?: string;
}

interface CreateLocationReportParams {
  locationId: number;
  serviceDate: string;
  reportData: any;
}

interface UpdateLocationReportParams {
  id: number;
  reportData: any;
}

interface QueryLocationReportsParams {
  skip?: number;
  take?: number;
  search?: string;
  orderBy?: string;
  orderDirection?: 'ASC' | 'DESC';
  locationId?: number;
}

interface QueryLocationReportsResponse {
  data: any[];
  total: number;
}

const generateLocationReportApi = async (params: GenerateReportParams): Promise<GenerateReportResponse> => {
  const response = await httpClient.post<GenerateReportResponse>('api/location/generate-report', params);
  return response.data;
};

const previewLocationReportApi = async (params: PreviewReportParams): Promise<GenerateReportResponse> => {
  const response = await httpClient.post<GenerateReportResponse>('api/location/preview-report', params);
  return response.data;
};

const getLocationReportsApi = async (): Promise<any[]> => {
  const response = await httpClient.get('api/location/get-reports', {});
  return response.data.data || response.data || [];
};

const createLocationReportApi = async (params: CreateLocationReportParams): Promise<any> => {
  const response = await httpClient.post('api/location/create-location-report', params);
  return response.data;
};

const updateLocationReportApi = async (params: UpdateLocationReportParams): Promise<any> => {
  const response = await httpClient.post('api/location/update-location-report', params);
  return response.data;
};

const queryLocationReportsApi = async (params: QueryLocationReportsParams): Promise<QueryLocationReportsResponse> => {
  const response = await httpClient.post<QueryLocationReportsResponse>('api/location/query-reports', params);
  return response.data;
};

export const useGenerateLocationReport = () => {
  return useMutation({
    mutationFn: generateLocationReportApi,
  });
};

export const usePreviewLocationReport = () => {
  return useMutation({
    mutationFn: previewLocationReportApi,
  });
};

export const useLocationReports = () => {
  return useQuery({
    queryKey: ['location-reports'],
    queryFn: getLocationReportsApi,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

export const useCreateLocationReport = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createLocationReportApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['location-reports'] });
    },
  });
};

export const useUpdateLocationReport = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateLocationReportApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['location-reports'] });
    },
  });
};

export const useQueryLocationReports = () => {
  return useMutation({
    mutationFn: queryLocationReportsApi,
  });
};
